import React from 'react';
import cn from 'classnames';
import moment from 'moment';

import timeInRangeOld from 'bl/scheduler/timeInRangeOld.bl';
import DndDraggable from 'components/dnd/DndDraggable.component';
import DndDroppable from 'components/dnd/DndDroppable.component';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { nowAsMoment } from 'utils/date/now.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import withOptions from 'utils/functions/withOptions.util';
import dnd from 'utils/react/dnd.util';

import { ScheduleProcedure } from 'scheduler/components/ScheduleProcedure';
import SchedulerCell from 'scheduler/components/SchedulerCell.component';
import { isInWorkingRange } from 'scheduler/utils';

import CN from './DeletedStylistColumn.module.scss';

const fromPropsToItems = withOptions(({ procedures, rangeByHours, salonTimezone }) => {
  const stack = {};

  return rangeByHours.map((currentTime) => {
    const isNow = timeInRangeOld(nowAsMoment(), currentTime, salonTimezone, 15);

    if (!stack[currentTime]) stack[currentTime] = 0; // TODO: solve this with .reduce() instead of .map()

    const currentProcedures = procedures
      .filter((p) => timeInRangeOld(p.start_time, currentTime, salonTimezone, 15))
      .map((p) => {
        const index = moment(currentTime);
        while (index.isBefore(moment(p.start_time).add(moment.duration(p.duration)))) {
          stack[index] = (stack[index] || 0) + 1;
          index.add(15, 'minutes');
        }

        return { key: `procedure-${p.id}`, procedure: p, stackOrder: stack[currentTime] };
      });

    return { currentProcedures, currentTime, isNow };
  });
});

const DeletedStylistColumn = (props) => {
  const { history, onCheckout, openingHours, salonId, updateProcedure, updateProcedureStatus } = props;
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  return (
    <div
      data-bem="DeletedStylistColumn"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div data-bem="DeletedStylistColumn__on-move" className="on-move">
        <div data-bem="DeletedStylistColumn__title" className="col-sticky">
          <div data-bem="DeletedStylistColumn__stylist-header" className={CN.columnHeader}>
            {t('scheduler.dayScheduler.deletedStaff')}
          </div>
        </div>

        <DndDroppable tag="DeletedStylistColumn--dnd-target" droppableId={dnd.dropCell({ stylist: { id: 0 } })}>
          {({ droppableProps, innerRef, placeholder }) => (
            <div
              data-bem="DeletedStylistColumn__droppable--el-target"
              style={{ width: '100%' }}
              className={cn({
                'cell-column': true,
                [CN.deletedStylistColumn]: true,
              })}
              ref={innerRef}
              {...droppableProps}
            >
              {placeholder}
              {fromPropsToItems(props).map(({ currentProcedures, currentTime, isNow }, indexProps) => (
                <SchedulerCell
                  stylist={{
                    stylistId: 'deleted',
                    email: '',
                    id: 0,
                    phone: '',
                    user_name: t('scheduler.dayScheduler.deleted'),
                    serves_as_waitlist: false,
                  }}
                  time={currentTime}
                  key={`cell-${currentTime.format('HH:mm')}-${indexProps}`}
                  droppable={false}
                  isInWorkingHour={isInWorkingRange(currentTime, openingHours)}
                  onClick={() => {}}
                  hovered={false}
                  dragOverTimeEl={null}
                  isDragging={false}
                >
                  <div data-bem="DeletedStylistColumn__cell" className={`${isNow ? 'current_time' : ''} current-entries`} style={{ display: 'flex' }}>
                    {currentProcedures.map(({ key, procedure, stackOrder }, index) => (
                      <DndDraggable tag="DeletedStylistColumn--dnd-source" draggableId={dnd.dragProcedure({ procedure })} key={key} index={index}>
                        {({ dragHandleProps, draggableProps, innerRef }, snapshot) => (
                          <div data-bem="DeletedStylistColumn--el-source" ref={innerRef} {...draggableProps} {...dragHandleProps}>
                            <ScheduleProcedure
                              allowModal={false}
                              categoriesAndServices={[]}
                              stylists={[]}
                              dayView
                              currentActiveProcedure={0}
                              markLinkedProcedures={alwaysNoop}
                              procedure={procedure}
                              key={key}
                              stackOrder={stackOrder}
                              currentTime={currentTime}
                              salonId={salonId}
                              history={history}
                              onCheckout={onCheckout}
                              updateProcedure={updateProcedure}
                              updateProcedureStatus={updateProcedureStatus}
                              isBeingDragged={snapshot.isDragging}
                              draggingFromDeletedColumMessage={snapshot.isDragging}
                            />
                          </div>
                        )}
                      </DndDraggable>
                    ))}
                  </div>
                </SchedulerCell>
              ))}
            </div>
          )}
        </DndDroppable>
      </div>
    </div>
  );
};

export default DeletedStylistColumn;
